import { useState } from 'react'
import {
  CheckCircle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  LogOut,
} from 'react-feather'
import clsx from 'clsx'

import { useAuthState } from '@pepper/ui'

import { OrgModalType } from 'container/Dashboard/Dashboard'
import useUserAccountType from 'utils/hooks/useUserAccountType'
import useOrganisations from 'utils/hooks/useOrganisations'

import MenuItem, { MenuItemProps } from './MenuItem'
import s from './UserProfile.module.css'

const DEFAULT_VISIBLE_COMPANIES = 5

type UserProfileProps = {
  onBack: () => void
  setJoinOrganisationModal?: (args: OrgModalType) => void
  onClose?: () => void
}

const UserProfile = ({
  onBack,
  setJoinOrganisationModal,
  onClose,
}: UserProfileProps) => {
  const [showAllOrgs, setShowAllOrgs] = useState(false)
  const { user, logout } = useAuthState()
  const accountType = useUserAccountType()
  const { selectedCompany, allCompanies, onSelect } = useOrganisations(
    user,
    setJoinOrganisationModal
  )
  const showViewAllOrgsButton =
    allCompanies && allCompanies.length >= DEFAULT_VISIBLE_COMPANIES

  const items = [
    {
      label: 'Personal Settings',
      path: '/personal-settings/profile',
      dataId: 'personalSettings',
    },
    {
      label: 'Organisation Settings',
      path: '/admin-console/organisation',
      dataId: 'adminConsole',
    },
    {
      label: 'Invoices',
      path: '/invoices',
      dataId: 'invoices',
    },
  ]

  const handleLogout = () => {
    logout()
    window.location.href = '/'
    window.localStorage.removeItem('user_alerts')
  }

  return (
    <nav className={s.navbar}>
      <button
        className={s.profileTrigger}
        type="button"
        data-id="business-topbar-avatar-button"
        onClick={onBack}
      >
        <ArrowLeft size={20} />
        <div>
          <h4 className={s.userName}>{user.first_name}</h4>
          <p className={s.userEmail}>{user.email}</p>
        </div>
      </button>

      <menu className={s.menu}>
        <div className={s.itemGroupName}>
          Organisations {allCompanies && `(${allCompanies?.length})`}
        </div>
        <div
          className={clsx(s.orgList, {
            [s.isExpanded]: showAllOrgs,
            [s.animate]: showViewAllOrgsButton,
          })}
        >
          {selectedCompany && (
            <div
              className={clsx(s.navLink, s.navLinkActive)}
              role="none"
              onClick={() => onSelect(selectedCompany)}
            >
              <span className={s.label}>{selectedCompany.company_name}</span>{' '}
              <CheckCircle size={16} />
            </div>
          )}

          {allCompanies
            ?.slice(0, DEFAULT_VISIBLE_COMPANIES - 1)
            .map((company: Company) => (
              <div
                key={company.id}
                className={s.navLink}
                role="none"
                onClick={() => onSelect(company)}
              >
                {company.company_name}
              </div>
            ))}

          {showViewAllOrgsButton &&
            showAllOrgs &&
            allCompanies?.slice(DEFAULT_VISIBLE_COMPANIES - 1).map(company => (
              <div
                key={company.id}
                className={s.navLink}
                data-id="business-topbar-organisation-list-item"
                role="none"
                onClick={() => onSelect(company)}
              >
                {company.company_name}
              </div>
            ))}
        </div>
        {showAllOrgs ? (
          <button
            className={s.viewAllButton}
            data-id="business-topbar-view-all-organisation-menu-item"
            onClick={() => setShowAllOrgs(false)}
          >
            View Fewer Organisations
            <ChevronUp size={16} />
          </button>
        ) : (
          <button
            className={s.viewAllButton}
            data-id="business-topbar-view-all-organisation-menu-item"
            onClick={() => setShowAllOrgs(true)}
          >
            View All Organisations
            <ChevronDown size={16} />
          </button>
        )}

        {items.map((item: MenuItemProps) => {
          return <MenuItem key={item.label} {...item} onClick={onClose} />
        })}
        <br />
        <div
          className={s.navLink}
          data-id="business-topbar-logout-menu-item"
          role="none"
          onClick={handleLogout}
        >
          <LogOut size={16} /> Logout
        </div>
      </menu>
    </nav>
  )
}

export default UserProfile
